import { testsEnabled } from '../sessionSettings'
import { completeTestResults, hiddenSafePythonRun, providedCodeOf, upgradeLegacyPythonHarness, withProvidedCode } from '../testResults'
import { isStudySession, parseTestCode } from '../../workspace/study/studyConfig'

// The Presenter's Run Tests (ExperimentPresenterTab): the program a student's Run Tests runs for
// the same code, and its test results read back. TerminalContext.runTestsPython is the source of
// truth: this makes the same choices - the same starter and harness, the same protections for
// hidden tests, a class exercise's checks each on a fresh run of the code in a study session -
// and a change there must be made here too.
//
// The program is built here, on the page's thread, and runs only in the Presenter's Web Worker
// (usePyodideWorker): a student's code run on the page itself could read the instructor's
// sign-in. The student page reads the results from the run's globals, which the worker does not
// hand back, so with tests on the program ends with a statement that prints them after a marker
// made anew for each run. readRunResults takes that line out of what the run printed: it is
// never shown.

/**
 * The exercise's session row - a condition's, as every condition runs the same exercise - as the
 * student page has it: test_code an object, also where the row stores it as a JSON string. Null
 * for no row.
 */
export const exerciseSession = (row) => (row ? { ...row, test_code: parseTestCode(row.test_code) } : null)

/**
 * Whether a run of the exercise is tested: as on the student page (testsEnabled), and only once
 * there is an exercise - before its row has loaded, Run runs the code alone.
 */
export const presenterHasTests = (exercise) => Boolean(exercise) && testsEnabled(exercise)

const MARKER_PREFIX = '__vizpi_presenter_results_'

/**
 * A marker for one run: random, so no student's code prints it by chance. Code that goes looking
 * for it while it runs can still print one; what guards the results is that only the last
 * marker counts, and only from a run that finished (readRunResults). A student's code could fake
 * its results on the student page just as well; the Presenter is for showing code to the class,
 * not for grading it.
 */
export const newRunMarker = () => {
  const bytes = new Uint8Array(12)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  }
  return `${MARKER_PREFIX}${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}__`
}

// The statement the program ends with: the marker and the run's results as JSON, on a line of
// their own - what readPythonResult (testResults.js) reads from the student page's run: `result`
// once the harness has started (__vizpi_harness_started is True), each entry a pass only when it
// is True; else none. Written to the output itself (file descriptor 1), after what the code
// printed, so code that replaced sys.stdout cannot hold it back; built-ins and modules through
// names of its own, as the harness reaches them, so nothing the student's code defined changes it.
const resultsStatement = (marker) => `def __vizpi_presenter_results():
  import builtins, json, os, sys
  names = builtins.globals()
  found = names.get('result') if names.get('__vizpi_harness_started') is True else None
  passes = [value is True for value in found] if builtins.isinstance(found, (builtins.list, builtins.tuple)) else []
  for stream in (sys.stdout, sys.__stdout__):
    try:
      stream.flush()
    except builtins.BaseException:
      pass
  line = (${JSON.stringify(marker)} + json.dumps(passes) + '\\n').encode('utf-8')
  while line:
    line = line[os.write(1, line):]
__vizpi_presenter_results()`

/**
 * The program the Presenter's worker runs for `code`: the student page's program for the
 * exercise (TerminalContext.runTestsPython) and, with tests on, the statement that prints its
 * results after `marker`. Tests off, or no exercise yet: the code alone - after the exercise's
 * provided code, if it has one - as the student page runs it with tests off.
 */
export const presenterRunSource = (exercise, code, marker) => {
  const session = exerciseSession(exercise)
  const userCode = String(code ?? '')
  const hasTests = presenterHasTests(session)
  const unitTest = session?.test_code?.unit_test || ''
  // From here on, as runTestsPython builds its combinedCode.
  const hiddenCode = hasTests ? upgradeLegacyPythonHarness(unitTest) : ''
  const starterCode = withProvidedCode(hasTests ? session?.test_code?.starter || '' : '', providedCodeOf(session?.test_code))
  const safeRun = hasTests
    ? hiddenSafePythonRun({
        starterCode,
        unitTest,
        testList: session?.test_list,
        freshStatePerCheck: isStudySession(session),
      })
    : null
  const program = safeRun ? safeRun.source(userCode) : `${starterCode}\n${userCode}\n${hiddenCode}`
  return hasTests ? `${program}\n${resultsStatement(marker)}` : program
}

/**
 * What a run printed, from the worker's answer ({ ok, stdout }): { output, results } - its output
 * without the marker's line, and the raw results that line holds, as readPythonResult would have
 * read them. Only a run that ran to its end printed them. One that raised or was stopped has
 * none, as on the student page when the student's code raises before the harness starts, and a
 * marker in its output is the student's code's own, left there. (A harness written by hand that
 * raises part-way is the one difference: the student page keeps the results before the error.
 * The generated harnesses catch every test's error, so they never do.)
 */
export const readRunResults = (answer, marker) => {
  const text = typeof answer?.stdout === 'string' ? answer.stdout : ''
  // The last: the statement runs after everything else the program prints.
  const at = answer?.ok && typeof marker === 'string' && marker ? text.lastIndexOf(marker) : -1
  if (at < 0) return { output: text, results: [] }
  const end = text.indexOf('\n', at)
  let results = []
  try {
    const parsed = JSON.parse(text.slice(at + marker.length, end < 0 ? text.length : end))
    if (Array.isArray(parsed)) results = parsed
  } catch (error) {
    // Not the list the statement prints: no results.
  }
  // What the code printed on that line before it (output with no line break at its end) stays.
  return { output: `${text.slice(0, at)}${end < 0 ? '' : text.slice(end + 1)}`, results }
}

/**
 * A run's test results as the student page has them (runTestsPython): with tests on, one pass or
 * fail per test of the exercise, a test without a result failed (completeTestResults); with tests
 * off, or no exercise yet, none.
 */
export const presenterTestResults = (results, exercise) =>
  presenterHasTests(exercise) ? completeTestResults(results, exercise?.test_list?.length) : []

/**
 * The line the student page's terminal ends a run's output with when it has test results
 * (TerminalContext.reportTestResults): the share of tests passed, as it writes it.
 */
export const testsCompletedText = (results) => {
  const passed = results.filter((result) => result === true).length
  const passrate = results.length > 0 ? (passed / results.length) * 100 : 0
  return `Tests Completed: ${passrate}%`
}
